/**
 * LibrarySettings — the entry window's Settings tab: an entry's settings in the
 * same form people know from the agent's Connectors tab (`ApiConnectorForm` /
 * `McpConnectorForm` in their "defaults" mode), drawn inside the window with
 * its buttons in the footer at the bottom — never a window over a window.
 *
 * How it signs in is not here: one line says what the Sign-in tab chose, with
 * the way to it (item 80) — the whole question is answered there.
 *
 * Two cases, one form:
 *   * this organization's own entry — everything is editable;
 *   * an entry shared from an organization above — only the fields a lower
 *     level may change (name, description, tool / action switches, guide,
 *     cache); the address, document and credential are shown read-only,
 *     "Set by <owner>". Saving stores this organization's version for itself
 *     and those under it; the owner keeps its own.
 *
 * What saving does is in the sticky bar while you edit: how many agents that
 * use the list's settings get the change, how many customized ones keep their
 * own, and the opt-in (off by default) to reset those too. The server is still
 * asked first (preview); a save that would leave agents needing their own key,
 * or move them off the list, says so in the bar and waits for "Save anyway".
 * The save names the version it was made from; if someone else saved since,
 * it is refused and the window offers to reload — never a silent overwrite.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { ApiConnectorForm, type ApiConnectorFormBackend } from '@modules/chatbots/components/ApiConnectorsSection';
import { McpConnectorForm, type McpConnectorFormBackend } from '@modules/chatbots/components/McpConnectorsSection';
import { SaveCancelled, phraseConnectorError, type FormFrame } from '@modules/chatbots/components/ConnectorDialog';
import {
  connectorLibraryService,
  errorBody,
  errorStatus,
  type ChangeOutcome,
  type ChangePreview,
  type LibraryEntryEditable,
} from '../services/connectorLibraryService';
import { ConflictDialog, lastChangeText } from './LibraryPanels';
import { SignInSummary } from '@modules/chatbots/components/SignInChoice';
import { storedSignIn } from '@modules/chatbots/utils/signIn';

interface Props {
  /** The organization whose list this is (the level being managed). */
  tenantId: string;
  detail: LibraryEntryEditable;
  /** Reload the entry (after a conflict) — the form remounts on what is stored now. */
  onReload: () => Promise<void>;
  onClose: () => void;
  /** Whether the form holds an edit not saved yet (the window asks before closing). */
  onDirty?: (dirty: boolean) => void;
  /** `outcome` is what the save did to agents. */
  onSaved: (entry: LibraryEntryEditable, outcome: ChangeOutcome | null) => void;
  /** Opens the entry's Sign-in tab. */
  onOpenSignIn: () => void;
}


/** The secret itself never goes to the preview: it only needs to know one was given. */
function withoutSecrets(payload: Record<string, unknown>): Record<string, unknown> {
  const { acknowledge_write: _ack, ...out } = payload;     // what a save does to agents does not depend on it
  if (out.headers) out.headers = {};
  if (out.auth) out.auth = {};
  return out;
}

const addOutcomes = (a: ChangeOutcome | null, b: ChangeOutcome | null): ChangeOutcome | null => (!a ? b : !b ? a : {
  updated: a.updated + b.updated, reset: a.reset + b.reset, detached: a.detached + b.detached,
  keys_needed: a.keys_needed + b.keys_needed,
});

type Pending = { preview: ChangePreview; resolve: (go: boolean) => void };

export function LibrarySettings({ tenantId, detail, onReload, onClose, onDirty, onSaved, onOpenSignIn }: Props) {
  const { t } = useLanguage();
  const [formKey, setFormKey] = useState(0);
  const [force, setForce] = useState(false);
  const forceRef = useRef(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [conflict, setConflict] = useState<{ changedBy?: string | null; changedAt?: string | null; changedAbove?: boolean } | null>(null);
  const outcome = useRef<ChangeOutcome | null>(null);
  const customized = detail.usage?.customized ?? 0;
  const following = detail.usage?.following ?? 0;

  const toggleForce = (on: boolean) => { forceRef.current = on; setForce(on); };

  const phrase = (err: unknown, fallbackKey: string): string => phraseConnectorError(t, err, fallbackKey);

  /** Preview → a save that costs agents something waits for "Save anyway" → save. */
  const save = useCallback(async (id: string, payload: Record<string, unknown>): Promise<LibraryEntryEditable> => {
    const preview = await connectorLibraryService.preview(id, tenantId, 'save', withoutSecrets(payload));
    if (preview.keys_needed > 0 || preview.detaches > 0) {
      const go = await new Promise<boolean>(resolve => setPending({ preview, resolve }));
      setPending(null);
      if (!go) throw new SaveCancelled();
    }
    try {
      const saved = await connectorLibraryService.save(id, tenantId, payload, detail.edit_version, forceRef.current,
        detail.inherited ? detail.parent_values : null);
      outcome.current = addOutcomes(outcome.current, saved.outcome);
      return saved.entry;
    } catch (err) {
      const body = errorBody(err);
      if (errorStatus(err) === 409 && body.field === 'version') {
        setConflict({ changedBy: body.changed_by, changedAt: body.changed_at, changedAbove: body.changed_above });
        throw new SaveCancelled();
      }
      throw err;
    }
  }, [detail, tenantId]);

  const apiBackend = useMemo<ApiConnectorFormBackend<LibraryEntryEditable>>(() => ({
    readSpec: (input) => connectorLibraryService.readSpec(input),
    create: () => Promise.reject(new Error('the entry window edits an entry; adding is the Add dialog')),
    update: save,
    refresh: async (id, specText) => {
      const refreshed = await connectorLibraryService.refresh(id, tenantId, specText);
      outcome.current = addOutcomes(outcome.current, refreshed.outcome);
      return refreshed.entry;
    },
  }), [tenantId, save]);

  const mcpBackend = useMemo<McpConnectorFormBackend<LibraryEntryEditable>>(() => ({
    create: () => Promise.reject(new Error('the entry window edits an entry; adding is the Add dialog')),
    update: save,
  }), [save]);

  const finish = (entry: LibraryEntryEditable) => {
    const done = outcome.current;
    outcome.current = null;
    toggleForce(false);
    onSaved(entry, done);
  };

  const reload = async () => {
    setConflict(null);
    await onReload();
    setFormKey(k => k + 1);
  };

  // The window's own frame: the form's body, then the footer.
  const frame: FormFrame = ({ note, noteTone, actions, acknowledge, children, testId, dirty = false }) => (
    <div className="cxl-settings" data-testid={testId}>
      <DirtyReport dirty={dirty} onDirty={onDirty} />
      <div className="cxl-settings__body">{children}</div>
      <div className="cxl-savebar" data-testid="library-savebar">
        <div className="cxl-savebar__facts">
          {pending ? (
            <ul className="cxl-savebar__warn" role="alert" data-testid="library-save-warning">
              {pending.preview.keys_needed > 0 && (
                <li>{t('connectorLibrary.impact.keysNeeded', { count: pending.preview.keys_needed })}</li>
              )}
              {pending.preview.detaches > 0 && (
                <li>{t('connectorLibrary.impact.detaches', { count: pending.preview.detaches })}</li>
              )}
            </ul>
          ) : !dirty ? (
            // Nothing to save yet: say so, rather than what a save would do.
            <span className="cxl-savebar__lead cxl-savebar__lead--quiet" data-testid="library-save-clean">
              {t('connectorLibrary.impact.noChanges')}
            </span>
          ) : (
            <>
              <span className="cxl-savebar__lead" data-testid="library-save-impact">
                {following > 0
                  ? t('connectorLibrary.impact.updatesLive', { count: following })
                  : t('connectorLibrary.impact.noFollowers')}
              </span>
              {customized > 0 && (
                <span className="cxl-savebar__sub">
                  {t('connectorLibrary.impact.customizedKeep', { count: customized })}{' '}
                  <label className="cxl-savebar__force">
                    <input type="checkbox" checked={force} onChange={e => toggleForce(e.target.checked)}
                           data-testid="library-save-force" />
                    <span>{t('connectorLibrary.impact.resetToo', { count: customized })}</span>
                  </label>
                </span>
              )}
            </>
          )}
          {note && !pending && (
            <span className={`cxl-savebar__note ${noteTone === 'error' ? 'is-error' : ''}`}
                  role={noteTone === 'error' ? 'alert' : undefined}>
              {note}
            </span>
          )}
          {!pending && acknowledge}
        </div>
        <div className="cxl-savebar__actions">
          {pending ? (
            <>
              <button type="button" className="btn-cancel" onClick={() => pending.resolve(false)}
                      data-testid="library-save-back">
                {t('connectorLibrary.impact.back')}
              </button>
              <button type="button" className="cx-btn cx-btn--primary cx-btn--lg" onClick={() => pending.resolve(true)}
                      data-testid="library-save-anyway">
                {t('connectorLibrary.impact.saveAnyway')}
              </button>
            </>
          ) : actions}
        </div>
      </div>
    </div>
  );

  const changed = lastChangeText(t, detail.last_change);
  const footerMeta: ReactNode = changed ? <span data-testid="library-editor-last-change">{changed}</span> : null;
  const layer = detail.inherited ? { ownerName: detail.owner_name || '' } : undefined;
  const signInSummary: ReactNode = (
    <div className="cxl-signin-line" data-testid="library-signin-summary">
      <span className="cxl-signin-line__label">{t('connectorLibrary.signin.summaryLabel')}</span>
      <span className="cxl-signin-line__value">
        <SignInSummary kind={detail.kind} value={storedSignIn(detail)} />
      </span>
      <button type="button" className="cxl-help__link cxl-signin-line__change" onClick={onOpenSignIn}
              data-testid="library-signin-summary-change">
        {t('connectorLibrary.signin.summaryChange')}
      </button>
    </div>
  );

  return (
    <>
      {detail.kind === 'api' ? (
        <ApiConnectorForm<LibraryEntryEditable>
          key={formKey}
          mode="defaults"
          frame={frame}
          draftScope={{ entryId: detail.entry_id, tenantId }}
          backend={apiBackend}
          existing={{
            id: detail.entry_id,
            name: detail.name,
            description: detail.description,
            base_url: detail.base_url || '',
            spec_url: detail.spec_url ?? null,
            spec_title: detail.spec_title ?? null,
            spec_version: detail.spec_version ?? null,
            operations: detail.operations || [],
            instructions: detail.instructions || '',
            auth_type: detail.auth_type || 'none',
            has_auth: detail.has_shared_credential,
            auth_meta: detail.auth_meta,
            cache_ttl_seconds: detail.cache_ttl_seconds ?? 3600,
            max_result_chars: detail.max_result_chars ?? 6000,
          }}
          signInSummary={signInSummary}
          layer={layer}
          footerMeta={footerMeta}
          onClose={onClose}
          onSaved={finish}
          phrase={phrase}
        />
      ) : (
        <McpConnectorForm<LibraryEntryEditable>
          key={formKey}
          mode="defaults"
          frame={frame}
          draftScope={{ entryId: detail.entry_id, tenantId }}
          backend={mcpBackend}
          existing={{
            id: detail.entry_id,
            name: detail.name,
            description: detail.description,
            url: detail.url || '',
            auth_header_name: detail.auth_header_name,
            has_shared_credential: detail.has_shared_credential,
            tools: detail.tools || [],
            instructions: detail.instructions || '',
          }}
          signInSummary={signInSummary}
          layer={layer}
          footerMeta={footerMeta}
          onClose={onClose}
          onSaved={finish}
          phrase={phrase}
        />
      )}
      {conflict && (
        <ConflictDialog changedBy={conflict.changedBy} changedAt={conflict.changedAt} changedAbove={conflict.changedAbove}
                        onClose={() => setConflict(null)} onReload={() => { void reload(); }} />
      )}
    </>
  );
}

/** Tells the window whether the form holds an unsaved edit (rendered inside
    the form's frame, which is where the form says so). */
function DirtyReport({ dirty, onDirty }: { dirty: boolean; onDirty?: (dirty: boolean) => void }) {
  useEffect(() => { onDirty?.(dirty); }, [dirty, onDirty]);
  return null;
}
